import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { currency } from "../App";
import { adminGet } from "../common/api/api";

const STATUS_COLORS = {
  pending:    "text-yellow-400 bg-yellow-400/10",
  processing: "text-blue-400 bg-blue-400/10",
  shipped:    "text-purple-400 bg-purple-400/10",
  delivered:  "text-green-400 bg-green-400/10",
  cancelled:  "text-red-400 bg-red-400/10",
};

const QUICK_LINKS = [
  { to: "/admin/addproduct", icon: "➕", label: "Add Product", color: "from-[#00d4ff] to-[#0099cc]" },
  { to: "/admin/orders",     icon: "🛒", label: "View Orders",  color: "from-[#7c3aed] to-[#5b21b6]" },
  { to: "/admin/coupons",    icon: "🏷️", label: "Coupons",      color: "from-[#f59e0b] to-[#d97706]" },
  { to: "/admin/reviews",    icon: "⭐", label: "Reviews",      color: "from-[#10b981] to-[#059669]" },
];

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const role = localStorage.getItem('user-role') || 'user';
  const userName = localStorage.getItem('user-name') || 'Admin';

  useEffect(() => {
    adminGet("/dashboardstats")
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-[#4a5568]">Loading dashboard...</div>
      </div>
    );
  }

  const STAT_CARDS = [
    {
      label: "Total Products",
      value: stats?.productCount ?? 0,
      icon: "📦",
      color: "from-[#00d4ff]/20 to-transparent",
      border: "border-[rgba(0,212,255,0.2)]",
      text: "text-[#00d4ff]",
      link: "/admin/listproduct",
    },
    {
      label: "Total Users",
      value: stats?.userCount ?? 0,
      icon: "👥",
      color: "from-[#7c3aed]/20 to-transparent",
      border: "border-purple-500/20",
      text: "text-purple-400",
      link: "/admin/users",
    },
    {
      label: "Total Orders",
      value: stats?.orderCount ?? 0,
      icon: "🛒",
      color: "from-[#f59e0b]/20 to-transparent",
      border: "border-yellow-500/20",
      text: "text-yellow-400",
      link: "/admin/orders",
    },
    {
      label: "Total Revenue",
      value: `${currency}${(stats?.revenue ?? 0).toLocaleString()}`,
      icon: "💰",
      color: "from-[#10b981]/20 to-transparent",
      border: "border-green-500/20",
      text: "text-green-400",
      link: "/admin/payments",
    },
  ];

  return (
    <div className="p-6 max-[640px]:p-4">
      {/* Welcome header */}
      <div className="mb-7">
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <h1 className="text-2xl font-bold text-white max-[640px]:text-xl">
            Welcome back, {userName} 👋
          </h1>
          <span
            className={`text-xs px-2.5 py-1 rounded-full font-bold border ${
              role === 'super-admin'
                ? 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30'
                : 'text-[#00d4ff] bg-[rgba(0,212,255,0.1)] border-[rgba(0,212,255,0.3)]'
            }`}
          >
            {role}
          </span>
        </div>
        <p className="text-sm text-[#4a5568]">Here's what's happening with your store today.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {STAT_CARDS.map(card => (
          <Link
            key={card.label}
            to={card.link}
            className={`relative overflow-hidden rounded-xl border p-5 bg-gradient-to-br ${card.color} ${card.border} hover:scale-[1.02] transition-transform`}
          >
            <div className="text-3xl mb-2">{card.icon}</div>
            <p className={`text-2xl font-black ${card.text}`}>{card.value}</p>
            <p className="text-xs text-[#4a5568] mt-1 font-medium">{card.label}</p>
          </Link>
        ))}
      </div>

      {/* Quick links */}
      <div className="mb-8">
        <h2 className="text-sm font-bold text-[#a0aec0] uppercase tracking-wider mb-3">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          {QUICK_LINKS.map(q => (
            <Link
              key={q.to}
              to={q.to}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r ${q.color} text-[#060c18] text-sm font-bold hover:opacity-90 transition-opacity max-[640px]:w-full max-[640px]:justify-center`}
            >
              <span>{q.icon}</span>
              <span>{q.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Bottom grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Recent Orders */}
        <div className="bg-white/[0.02] border border-white/[0.07] rounded-xl p-5 max-[640px]:p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-white">Recent Orders</h2>
            <Link to="/admin/orders" className="text-xs text-[#00d4ff] hover:underline">View all</Link>
          </div>
          {!stats?.recentOrders?.length ? (
            <p className="text-xs text-[#4a5568] py-4 text-center">No orders yet</p>
          ) : (
            <div className="space-y-2.5">
              {stats.recentOrders.map(order => (
                <div key={order._id} className="flex items-center justify-between gap-3 py-2 border-b border-white/[0.04] last:border-0 max-[480px]:items-start max-[480px]:flex-col">
                  <div className="min-w-0">
                    <p className="text-sm text-[#e2e8f0] font-medium">{order.userName || "Guest"}</p>
                    <p className="text-xs text-[#4a5568]">
                      {order.orderId || order._id?.toString().slice(-6)}
                      {" · "}
                      {new Date(order.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right max-[480px]:text-left max-[480px]:w-full">
                    <p className="text-sm font-bold text-white">{currency}{order.totalAmount}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[order.status] || STATUS_COLORS.pending}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Signups */}
        <div className="bg-white/[0.02] border border-white/[0.07] rounded-xl p-5 max-[640px]:p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-white">Recent Signups</h2>
            <Link to="/admin/users" className="text-xs text-[#00d4ff] hover:underline">View all</Link>
          </div>
          {!stats?.recentUsers?.length ? (
            <p className="text-xs text-[#4a5568] py-4 text-center">No users yet</p>
          ) : (
            <div className="space-y-2.5">
              {stats.recentUsers.map(user => (
                <div key={user._id} className="flex items-center gap-3 py-2 border-b border-white/[0.04] last:border-0 max-[480px]:items-start">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#7c3aed] flex items-center justify-center text-[#060c18] text-xs font-black shrink-0">
                    {(user.name || user.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#e2e8f0] font-medium truncate">{user.name || "(no name)"}</p>
                    <p className="text-xs text-[#4a5568] truncate">{user.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-[#4a5568]">
                      {new Date(user.date).toLocaleDateString()}
                    </p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${
                      user.role === 'super-admin' ? 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' :
                      user.role === 'admin' ? 'text-[#00d4ff] bg-[rgba(0,212,255,0.1)] border-[rgba(0,212,255,0.3)]' :
                      'text-[#4a5568] border-white/[0.08]'
                    }`}>
                      {user.role || 'user'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
